import { useAuth } from '@agentnet/auth';
import { IconButton, Typography } from '@material-ui/core';
import { cancelIdentityVerificationRequest, savePhoneNumber } from 'api/file/file-api';
import {
  ContactValidationResult,
  ICancelRequestPayload,
  ICancelRequestResponse,
  IdentityVerificationResult,
  ISavePhoneNumberPayload,
  Party,
  PartyPayload,
} from 'api/file/interfaces/get-file-order-insights';
import clsx from 'clsx';
import {
  cancelledText,
  CONTACT_VALIDATION_INFO,
  CONTACT_VALIDATION_MESSAGE,
  MAX_ATTEMPTS_VERIFICATION_TEXT,
  MAX_IDENTITY_CHECK_ATTEMPTS,
  offerMessage,
  pendingAwaitingText,
  pendingText,
  RETRY_VERIFICATION_TEXT,
  TRANSACTION_MESSAGE,
  VERIFICATION_TIME_LIMIT_MESSAGE,
} from 'features/help/constants';
import useAsync from 'hooks/useAsync';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import HourglassTopOutlined from 'ui-kit/icons/HourglassTopOutlined';
import { theme } from 'ui-kit/theme/theme';
import { formatDate, getVerificationExpiryTime, isPastExpiry } from 'utilities/dateFormatter';
import { TransactionStatusEnum } from 'utilities/verification/types';
import ContactInfoDisplay from './ContactInfoDisplay';
import useStyles from './ListStyles';
import OrderInsightAlert from './OrderInsightAlerts';
import PhoneNumberDetails from './PhoneNumberDetails';
import VerificationResults from './VerificationResults';

interface IVerifyPhoneNumber {
  qaPrefix: string;
  index: number;
  type: 'ContactValidation' | 'EnhancedIdentityVerification';
  party: Party;
  description: string;
  hasReadOnlyFraudValidation?: boolean;
  hasFraudValidation?: boolean;
  identityResults?: IdentityVerificationResult | null;
  partyInfo: PartyPayload;
  validationChecks: {
    validateContactInfoStatusResults: ContactValidationResult | null;
    doValidateContactInfo: (...args: any[]) => Promise<void>;
    enhancedIdentityVerification: (...args: any[]) => Promise<void>;
    enhancedIdentityVerificationStatus: 'idle' | 'pending' | 'success' | 'error';
    updateTransactionStatus: (updatedFields: { transactionStatus: TransactionStatusEnum }) => void;
    cancelPartyVerification?: (payload: ICancelRequestPayload) => Promise<void>;
    cancelPartyVerificationStatus?: 'idle' | 'pending' | 'success' | 'error';
  };
  syncPartyPhoneOrEmail: (updatedFields?: Partial<Party>, hasFieldRendered?: boolean) => void | boolean;
  updatedPartyInfo?: (updatedFields?: Partial<Party>) => void;
  isDrawer?: boolean;
  validateFileErrors?: (currentParty: Party) => Promise<boolean>;
  showValidation?: (show: boolean) => void;
  isPartyVerification?: boolean;
  isFieldsDisabled?: boolean;
  underwriterAlert?: ReactNode;
}

const VerifyPhoneNumber = ({
  qaPrefix,
  description,
  type = 'ContactValidation',
  party,
  validationChecks,
  hasReadOnlyFraudValidation,
  hasFraudValidation,
  index,
  identityResults = null,
  partyInfo,
  isDrawer = false,
  syncPartyPhoneOrEmail,
  updatedPartyInfo,
  validateFileErrors,
  showValidation,
  isPartyVerification = false,
  isFieldsDisabled = false,
  underwriterAlert,
}: IVerifyPhoneNumber) => {
  const {
    doValidateContactInfo,
    enhancedIdentityVerification,
    validateContactInfoStatusResults,
    enhancedIdentityVerificationStatus,
    updateTransactionStatus,
    cancelPartyVerification,
    cancelPartyVerificationStatus,
  } = validationChecks;
  const classes = useStyles();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
  const [cancelRequestSubmitted, setCancelRequestSubmitted] = useState<boolean>(false);

  const isContactValidation = type === 'ContactValidation';

  const { addGlobalMsg } = useGlobalMessages();
  const { getAccessToken } = useAuth();

  const services = async (payload: Party, remainingFields: Partial<Party>): Promise<ISavePhoneNumberPayload> => {
    const token = await getAccessToken();
    const currentPayload = {
      firmId: partyInfo.firmId,
      fileId: partyInfo.fileId,
      partyId: payload.id,
      partyType: payload.partyType,
      party: payload.filePartyType,
      isSpouse: payload.isSpouse,
      address: payload.address,
      suffix: payload.suffix ?? '',
      ...remainingFields,
    };
    return await savePhoneNumber(currentPayload, token);
  };

  const {
    execute: saveModalDetails,
    status: saveModalDetailsStatus,
    errors: saveModalDetailsErrors,
  } = useAsync<ISavePhoneNumberPayload>(services, false);

  const cancelRequest = async (payload: ICancelRequestPayload) => {
    const token = await getAccessToken();
    const currentPayload: ICancelRequestPayload = {
      transactionId: payload.transactionId,
      fileId: payload.fileId,
    };
    return await cancelIdentityVerificationRequest(currentPayload, token);
  };

  const {
    execute: cancelRequestExecute,
    status: cancelRequestStatus,
    errors: cancelRequestErrors,
    value: cancelRequestResult,
  } = useAsync<ICancelRequestResponse>(cancelRequest, false);

  useEffect(() => {
    if (saveModalDetailsStatus === 'error' || cancelRequestStatus === 'error') {
      const errorMessages = [...(saveModalDetailsErrors ?? []), ...(cancelRequestErrors ?? [])];
      if (errorMessages?.length) {
        errorMessages.map((err) => {
          addGlobalMsg({
            message: err,
            type: 'error',
          });
        });
      }
    }
  }, [saveModalDetailsStatus, cancelRequestStatus]);

  // only recalculate on spcific party keys
  const modifiedParty = useMemo(() => {
    const tempParty = { ...party };
    if (party.address?.isForeignAddress) {
      tempParty.address = {
        isForeignAddress: false,
        address1: '',
        address2: '',
        city: '',
        postalCode: '',
        stateOrProvince: '',
      };
    }
    return tempParty;
  }, [
    party.address?.isForeignAddress,
    party.phoneNumber,
    party.emailAddress,
    party?.fileNumber,
    party?.underwriterCode,
    party?.personalInformation,
  ]);

  const validatePhoneNumberDetails = (newValue: Party, verifyWithEmail: boolean) => {
    const { phoneNumber, emailAddress, ...rest } = newValue;

    const remainingFields = isContactValidation
      ? { phone: phoneNumber, email: emailAddress }
      : {
          verifyWithEmail,
          ...(verifyWithEmail ? { email: emailAddress } : { phone: phoneNumber }),
        };

    if (isDrawer) {
      const payload = {
        firmId: partyInfo.firmId,
        fileNumber: party.fileNumber,
        underwriter: party.underwriterCode,
        ...party.personalInformation,
        ...remainingFields,
        ...rest,
        ...(partyInfo.fileId && { fileId: partyInfo.fileId }),
      };
      setShowLoader(true);

      if (type === 'ContactValidation') {
        doValidateContactInfo(payload, true).finally(() => {
          setShowLoader(false);
        });
      } else {
        enhancedIdentityVerification(payload, true).finally(() => {
          setShowLoader(false);
        });
      }
    } else {
      const { suffix = '', ...remainingDetails } = rest;
      const updatedPartyInfo = {
        ...partyInfo,
        ...remainingDetails,
        ...remainingFields,
      };
      setShowLoader(true);
      saveModalDetails(newValue, remainingFields)
        .then(() =>
          type === 'ContactValidation'
            ? doValidateContactInfo(updatedPartyInfo, false)
            : enhancedIdentityVerification({ ...updatedPartyInfo, suffix }),
        )
        .then(() => {
          setShowLoader(false);
        })
        .catch(() => {
          setShowLoader(false);
        });
    }
  };

  const renderIdentityVerification = (isMarginTopLarge?: boolean) => {
    return (
      <>
        {!isDrawer && (
          <>
            <Typography
              variant="h6"
              className={clsx(
                classes.marginBottom,
                isMarginTopLarge ? classes.marginTopLarge : classes.marginTopSmall,
                classes.transactionMessageContainer,
              )}
            >
              {TRANSACTION_MESSAGE}
            </Typography>
            <Typography variant="body1" className={clsx(classes.marginBottomLarge, classes.typographyFontSize)}>
              {VERIFICATION_TIME_LIMIT_MESSAGE}
            </Typography>
          </>
        )}
        <PhoneNumberDetails
          qaPrefix={qaPrefix}
          index={index}
          disablePrimary={
            !(hasFraudValidation || hasReadOnlyFraudValidation) ||
            (identityResults?.attempt ?? 0) >= MAX_IDENTITY_CHECK_ATTEMPTS
          }
          isActionInProgress={showLoader || enhancedIdentityVerificationStatus === 'pending'}
          onConfirm={validatePhoneNumberDetails}
          containerClassName={isDrawer && isMarginTopLarge ? classes.marginTopLarge : undefined}
          syncPartyPhoneOrEmail={syncPartyPhoneOrEmail}
          updatedPartyInfo={updatedPartyInfo}
          type={type}
          party={modifiedParty}
          validateFileErrors={validateFileErrors}
          showValidation={showValidation}
          isDrawer={isDrawer}
          disabled={isFieldsDisabled}
          underwriterAlert={underwriterAlert}
          hasAttemptErrors={
            identityResults?.transactionStatus ===
              (isDrawer ? TransactionStatusEnum.Cancelled : TransactionStatusEnum.Failed) &&
            (identityResults?.attempt ?? 0) > 0
          }
        />
      </>
    );
  };

  const nonAdminOfferMessage = useMemo(() => {
    if (!hasFraudValidation && !hasReadOnlyFraudValidation) {
      return (
        <Typography variant="body1" className={clsx(classes.marginBottom, classes.typographyFontSize)}>
          {`${description} ${offerMessage['nonAdmin']}`}
        </Typography>
      );
    }
    return null;
  }, [hasFraudValidation, hasReadOnlyFraudValidation, description]);

  // if contact validation is enabled
  if (isContactValidation) {
    const contactInfo = party.contactValidation || validateContactInfoStatusResults;
    const isTransactionStatusFailed =
      party.contactValidation?.transactionStatus === TransactionStatusEnum.Failed ||
      validateContactInfoStatusResults?.transactionStatus === TransactionStatusEnum.Failed;
    if (contactInfo && !isTransactionStatusFailed) {
      return <ContactInfoDisplay {...contactInfo} />;
    }

    if (nonAdminOfferMessage) return nonAdminOfferMessage;

    return (
      <>
        {!isDrawer && (
          <>
            <Typography
              variant="h6"
              className={clsx(classes.marginBottom, classes.marginTopSmall, classes.transactionMessageContainer)}
            >
              {CONTACT_VALIDATION_MESSAGE}
            </Typography>
            <Typography variant="body1" className={clsx(classes.marginBottomLarge, classes.typographyFontSize)}>
              {CONTACT_VALIDATION_INFO}
            </Typography>
          </>
        )}
        <PhoneNumberDetails
          qaPrefix={qaPrefix}
          index={index}
          disablePrimary={!(hasFraudValidation || hasReadOnlyFraudValidation)}
          isActionInProgress={showLoader}
          onConfirm={validatePhoneNumberDetails}
          validateFileErrors={validateFileErrors}
          type={type}
          party={modifiedParty}
          syncPartyPhoneOrEmail={syncPartyPhoneOrEmail}
          updatedPartyInfo={updatedPartyInfo}
          showValidation={showValidation}
          isDrawer={isDrawer}
          disabled={isFieldsDisabled}
          underwriterAlert={underwriterAlert}
        />
      </>
    );
  }

  const lastAttempt = identityResults?.attempt === 2 ? 'time' : 'times';
  const isIdentityResultStatusCompleted = identityResults?.transactionStatus === TransactionStatusEnum.Completed;

  if (isIdentityResultStatusCompleted) {
    const { summary } = identityResults;
    const { transactionDate } = summary ?? {};
    const isOlderThan90Days = isPastExpiry(transactionDate);

    return (
      <>
        <VerificationResults
          {...identityResults}
          fileId={partyInfo.fileId}
          index={index}
          qaPrefix={qaPrefix}
          isPartyVerification={isPartyVerification}
          isDrawer={isDrawer}
        />
        {isOlderThan90Days && renderIdentityVerification(true)}
      </>
    );
  }

  if (nonAdminOfferMessage) return nonAdminOfferMessage;

  if (identityResults?.transactionStatus === TransactionStatusEnum.AwaitingUserAction) {
    const handleCancelRequest = () => {
      setOpenCancelModal((prev) => !prev);
    };

    const handleCancelStatus = async () => {
      if (!identityResults?.transactionId || !partyInfo.fileId) {
        return;
      }

      const payload = { transactionId: identityResults.transactionId, fileId: partyInfo.fileId };
      setCancelRequestSubmitted(true);

      try {
        if (isDrawer && cancelPartyVerification) {
          await cancelPartyVerification(payload);
        } else {
          await cancelRequestExecute(payload);
        }
        setCancelRequestSubmitted(false);
        setOpenCancelModal(false);
        updateTransactionStatus({ transactionStatus: TransactionStatusEnum.UserCancelled });
      } catch (error) {
        setCancelRequestSubmitted(false);
      }
    };

    const action: ReactNode = (
      <IconButton
        aria-label="close"
        color="inherit"
        size="small"
        disabled={!(hasFraudValidation || hasReadOnlyFraudValidation)}
        onClick={handleCancelRequest}
        data-testid={`${qaPrefix}-button-cancel-request${isDrawer ? '' : `-${index}`}`}
      >
        <Typography className={classes.bold}>Cancel Request</Typography>
      </IconButton>
    );
    return (
      <>
        <AgentNetConfirmationDialog
          onConfirm={handleCancelStatus}
          open={openCancelModal}
          onDismissAction={handleCancelRequest}
          disabled={cancelRequestSubmitted}
          dialogTitle="Confirm Cancellation"
          dialogBtnContent="Yes, Cancel Request"
          dialogCancelBtnContent="Keep Request"
          updatedQAPrefix={{
            close: `${qaPrefix}-button-close${isDrawer ? '' : `-${index}`}`,
            cancel: `${qaPrefix}-button-keep-request${isDrawer ? '' : `-${index}`}`,
            submit: `${qaPrefix}-button-yes-cancel-request${isDrawer ? '' : `-${index}`}`,
          }}
          dialogText={cancelledText}
        />
        <VerificationResults {...identityResults} index={index} showRequestedDataOnly qaPrefix={qaPrefix} />
        <OrderInsightAlert
          alertWithContent={[
            {
              content: (
                <>
                  <strong>Verification Pending </strong>
                  <p className={classes.pendingTexts}>{pendingAwaitingText}</p>
                </>
              ),
              action,
              severity: 'warning',
              icon: <HourglassTopOutlined fill={theme.palette.status.pending} style={{ transform: 'scale(0.9)' }} />,
            },
          ]}
        />
      </>
    );
  }

  if (identityResults?.transactionStatus === TransactionStatusEnum.Pending) {
    return (
      <>
        <VerificationResults {...identityResults} index={index} showRequestedDataOnly qaPrefix={qaPrefix} />
        <OrderInsightAlert
          alertWithContent={[
            {
              content: (
                <>
                  <strong>Pending Response from Party</strong>
                  <p className={classes.pendingTexts}>
                    {isDrawer
                      ? `Verification request sent at ${formatDate(
                          identityResults?.submitted ?? '',
                          undefined,
                          'custom',
                          'h:mm:ss a',
                        )}. Expires at ${getVerificationExpiryTime(identityResults?.submitted ?? '', 30, true)}.`
                      : pendingText.replace('{time}', getVerificationExpiryTime(identityResults?.submitted ?? ''))}
                  </p>
                </>
              ),
              severity: 'warning',
              icon: <HourglassTopOutlined fill={theme.palette.status.pending} style={{ transform: 'scale(0.9)' }} />,
            },
          ]}
        />
      </>
    );
  }

  const showAttemptErrors =
    identityResults?.transactionStatus &&
    [
      TransactionStatusEnum.UserCancelled,
      isDrawer ? TransactionStatusEnum.Cancelled : TransactionStatusEnum.Failed,
    ].includes(identityResults?.transactionStatus);

  const renderAttemptError = () => {
    if (showAttemptErrors && identityResults?.attempt) {
      return (
        <div className={identityResults.attempt >= MAX_IDENTITY_CHECK_ATTEMPTS ? '' : classes.marginTopLarge}>
          <OrderInsightAlert
            errorMessages={[
              `${
                identityResults.attempt >= MAX_IDENTITY_CHECK_ATTEMPTS
                  ? MAX_ATTEMPTS_VERIFICATION_TEXT
                  : RETRY_VERIFICATION_TEXT.replace(
                      'count',
                      (MAX_IDENTITY_CHECK_ATTEMPTS - identityResults.attempt).toString(),
                    ).replace('times', lastAttempt)
              }`,
            ]}
            isPdf={false}
            hasErrorMessagesHtml
          />
        </div>
      );
    }

    return null;
  };

  if (showAttemptErrors && (identityResults?.attempt ?? 0) >= MAX_IDENTITY_CHECK_ATTEMPTS) {
    return renderAttemptError();
  }

  return (
    <>
      {renderIdentityVerification()}
      {renderAttemptError()}
    </>
  );
};

export default VerifyPhoneNumber;
